import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FileText, Scale, Users, AlertTriangle, CheckCircle } from 'lucide-react';

const TermsOfService = () => {
  const lastUpdated = "November 13, 2025";

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Helmet>
        <title>Terms of Service | KGOB CPAs & Advisors</title>
        <meta name="description" content="Terms of Service for Kohari Gonzalez Oneyear & Brown CPAs & Advisors professional accounting services in North Carolina." />
        <link rel="canonical" href="https://kgob.com/terms-of-service" />
      </Helmet>
      <Header />
      
      <main className="py-16 lg:py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#D4AF37]/10 mb-6">
              <Scale className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <h1 className="font-['Playfair_Display'] text-4xl lg:text-5xl font-bold text-[#0B2240] mb-4">
              Terms of Service
            </h1>
            <p className="text-lg text-[#4C5A70]">
              Last Updated: {lastUpdated}
            </p>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-[0_8px_30px_rgba(11,34,64,0.12)]">
              
              {/* Acceptance */}
              <section className="mb-10">
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4 flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-[#D4AF37]" />
                  Acceptance of Terms
                </h2>
                <p className="text-[#4C5A70] leading-relaxed mb-4">
                  By accessing or using the website <a data-testid="terms-of-service-a-3" href="https://kgob.com" className="text-[#D4AF37] hover:underline">kgob.com</a>, requesting our services, or engaging with Kohari Gonzalez Oneyear & Brown CPAs & Advisors ("KGOB," "we," "our," or "us"), you agree to be bound by these Terms of Service and our Privacy Policy.
                </p>
                <p className="text-[#4C5A70] leading-relaxed">
                  KGOB is a professional accounting firm organized under the laws of North Carolina and registered with the North Carolina Board of CPA Examiners. Our services are subject to professional standards established by the American Institute of Certified Public Accountants (AICPA) and applicable state regulations.
                </p>
              </section>

              {/* Services Overview */}
              <section className="mb-10">
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4 flex items-center gap-3">
                  <FileText className="w-6 h-6 text-[#D4AF37]" />
                  Professional Services
                </h2>
                <p className="text-[#4C5A70] mb-3">
                  KGOB provides professional accounting, tax, and advisory services including but not limited to:
                </p>
                <ul className="list-disc list-inside text-[#4C5A70] space-y-2 mb-4">
                  <li>Tax preparation and planning</li>
                  <li>Business valuation and growth strategy</li>
                  <li>IRS representation and tax problem resolution</li>
                  <li>Accounting and bookkeeping services</li>
                  <li>Financial advisory and consulting</li>
                  <li>Estate and succession planning</li>
                </ul>
                <div className="bg-[#FFF9E6] border-l-4 border-[#D4AF37] p-6 rounded-lg">
                  <p className="text-[#0B2240] font-semibold mb-2">
                    Important Note: Engagement Letters Required
                  </p>
                  <p className="text-[#4C5A70]">
                    Specific professional services require a signed engagement letter that defines the scope, fees, responsibilities, and deliverables. These Terms of Service are supplemental to, not a replacement for, individual engagement agreements.
                  </p>
                </div>
              </section>

              {/* Client Responsibilities */}
              <section className="mb-10">
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4 flex items-center gap-3">
                  <Users className="w-6 h-6 text-[#D4AF37]" />
                  Client Responsibilities
                </h2>
                <p className="text-[#4C5A70] mb-3">
                  When engaging our services, you agree to:
                </p>
                <ul className="list-disc list-inside text-[#4C5A70] space-y-2">
                  <li>Provide complete, accurate, and timely information necessary for us to perform services</li>
                  <li>Respond promptly to requests for information or clarification</li>
                  <li>Maintain records and documents as required by law and professional standards</li>
                  <li>Review deliverables (tax returns, financial statements, etc.) before approval</li>
                  <li>Pay fees as agreed in the engagement letter</li>
                  <li>Notify us of any changes in circumstances that may affect our services</li>
                </ul>
              </section>

              {/* Professional Standards */}
              <section className="mb-10">
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4">
                  Professional Standards and Ethics
                </h2>
                <p className="text-[#4C5A70] mb-3">
                  KGOB adheres to:
                </p>
                <ul className="list-disc list-inside text-[#4C5A70] space-y-2">
                  <li>AICPA Code of Professional Conduct</li>
                  <li>North Carolina Board of CPA Examiners rules (21 NCAC 08N)</li>
                  <li>Generally Accepted Auditing Standards (GAAS)</li>
                  <li>Statements on Standards for Tax Services (SSTS)</li>
                  <li>Statements on Standards for Accounting and Review Services (SSARS)</li>
                </ul>
              </section>

              {/* Limitations and Disclaimers */}
              <section className="mb-10">
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4 flex items-center gap-3">
                  <AlertTriangle className="w-6 h-6 text-[#D4AF37]" />
                  Limitations and Disclaimers
                </h2>
                
                <h3 className="text-xl font-semibold text-[#0B2240] mt-6 mb-3">No Guarantee of Results</h3>
                <p className="text-[#4C5A70] mb-4">
                  While we strive for excellence, we cannot guarantee specific outcomes, tax savings, or financial results. Tax laws and regulations are subject to change and interpretation.
                </p>

                <h3 className="text-xl font-semibold text-[#0B2240] mt-6 mb-3">General Information Only</h3>
                <p className="text-[#4C5A70] mb-4">
                  Content on our website, including blog posts, guides, and resources, is for general educational purposes only and does not constitute professional advice. Do not act on such information without consulting a qualified professional about your specific situation.
                </p>

                <h3 className="text-xl font-semibold text-[#0B2240] mt-6 mb-3">Limitation of Liability</h3>
                <p className="text-[#4C5A70] mb-4">
                  To the maximum extent permitted by law, KGOB's liability for any claim arising from our services shall be limited to the total fees paid by the client for the specific service that gave rise to the claim, or for pro bono services, $1,000, whichever is greater.
                </p>
                <p className="text-[#4C5A70]">
                  We are not liable for indirect, consequential, special, or punitive damages, including lost profits or business opportunities.
                </p>
              </section>

              {/* Intellectual Property */}
              <section className="mb-10">
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4">
                  Intellectual Property
                </h2>
                <p className="text-[#4C5A70] mb-3">
                  All content on kgob.com, including text, graphics, logos, software, and proprietary tools (AskSara.cpa, BizHub.com, LetsTalkGrowth.com, LetsTalkStrategy.io, LetsTalkRelief.com), is the property of KGOB and protected by copyright, trademark, and other intellectual property laws.
                </p>
                <p className="text-[#4C5A70]">
                  "Let's Talk Growth®" is a registered trademark of KGOB. You may not use our trademarks without prior written permission.
                </p>
              </section>

              {/* Termination */}
              <section className="mb-10">
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4">
                  Termination
                </h2>
                <p className="text-[#4C5A70] mb-3">
                  Either party may terminate an engagement as specified in the engagement letter, subject to:
                </p>
                <ul className="list-disc list-inside text-[#4C5A70] space-y-2">
                  <li>Payment of fees for services rendered through the termination date</li>
                  <li>Return of client records and documents</li>
                  <li>Completion of any filings or submissions in progress (if applicable)</li>
                  <li>Professional obligations under AICPA standards</li>
                </ul>
              </section>

              {/* Dispute Resolution */}
              <section className="mb-10">
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4">
                  Dispute Resolution
                </h2>
                <p className="text-[#4C5A70] mb-3">
                  In the event of a dispute arising from our services:
                </p>
                <ul className="list-disc list-inside text-[#4C5A70] space-y-2 mb-4">
                  <li><strong>Good Faith Discussion:</strong> Both parties agree to first attempt resolution through direct communication</li>
                  <li><strong>Mediation:</strong> If unresolved, disputes shall be submitted to mediation in Charlotte, North Carolina</li>
                  <li><strong>Arbitration:</strong> If mediation fails, binding arbitration under the American Arbitration Association rules</li>
                  <li><strong>Statute of Limitations:</strong> Any claim must be commenced within 2 years of service delivery, regardless of any longer statutory period</li>
                </ul>
              </section>

              {/* Governing Law */}
              <section className="mb-10">
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4">
                  Governing Law
                </h2>
                <p className="text-[#4C5A70]">
                  These Terms of Service are governed by and construed in accordance with the laws of the State of North Carolina, without regard to its conflict of law provisions. Any legal action or proceeding shall be brought exclusively in the state or federal courts located in Mecklenburg County, North Carolina.
                </p>
              </section>

              {/* Severability */}
              <section className="mb-10">
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4">
                  Severability
                </h2>
                <p className="text-[#4C5A70]">
                  If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
                </p>
              </section>

              {/* Changes */}
              <section className="mb-10">
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4">
                  Changes to Terms
                </h2>
                <p className="text-[#4C5A70]">
                  We reserve the right to modify these Terms of Service at any time. We will notify clients of material changes by email or website notice. Your continued use of our services after changes constitutes acceptance of the modified terms.
                </p>
              </section>

              {/* Contact */}
              <section>
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0B2240] mb-4">
                  Questions About These Terms
                </h2>
                <p className="text-[#4C5A70] mb-4">
                  If you have questions about these Terms of Service, please contact:
                </p>
                <div className="bg-[#F7F6F2] p-6 rounded-lg">
                  <p className="text-[#0B2240] font-semibold mb-2">Kohari Gonzalez Oneyear & Brown CPAs & Advisors</p>
                  <p className="text-[#4C5A70] text-sm">
                    2740 East WT Harris Blvd, Suite 200<br />
                    Charlotte, NC 28213<br />
                    Phone: <a data-testid="terms-of-service-a-2" href="tel:7045993355" className="text-[#D4AF37] hover:underline">(704) 599-3355</a><br />
                    Email: <a data-testid="terms-of-service-a-1" href="mailto:info@kgob.com" className="text-[#D4AF37] hover:underline">info@kgob.com</a>
                  </p>
                </div>
              </section>

              {/* Professional Disclaimer */}
              <div className="mt-10 pt-6 border-t border-gray-200">
                <p className="text-sm text-[#4C5A70] italic">
                  <strong>Professional Disclaimer:</strong> These Terms of Service are general in nature. Specific professional engagements require a signed engagement letter that supersedes these general terms where they conflict. This document does not create an accountant-client relationship. Such relationships are established only through executed engagement letters.
                </p>
              </div>

            </div>
          </motion.div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default TermsOfService;
